import { useMemo } from 'react';
import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { Page } from '@frontend/components';
import { useHasBulkEmailAccess, useHasBulkTextAccess } from '@frontend/feature-access';
import { Header } from '@frontend/header-navigation';
import { useTranslation } from '@frontend/i18n';
import { useMessagingFeaturesEnabled } from '@frontend/messaging-enabled-hook';
import { BulkEmailPrefixes, BulkTextPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { Tabs } from '@frontend/design-system';
import { CampaignList } from '../components/campaign-list/campaign-list';
import { DomainVerifyBanner } from '../components/domain-verify-banner/domain-verify-banner';
import { EmptyStateNoAccess } from '../components/empty-state-no-access';
import { EmptyStateNoFeatures } from '../components/empty-state-no-features';
import { useBulkNavigator } from '../hooks/use-bulk-navigator';

const TABS_ID = 'bulk-tabs-';

type Props = {
  campaignType?: CampaignType;
};

// TODO: Update to new Page component
export const Campaigns = ({ campaignType: passedCampaignType = CampaignType.EMAIL }: Props) => {
  const { t } = useTranslation('bulk-messaging');

  const { hasBulkEmailAccess, hasBulkEmailFlagEnabled } = useHasBulkEmailAccess();
  const { hasBulkTextAccess: hasBulkTextAccessRaw, hasBulkTextFlagEnabled: hasBulkTextFlagEnabledRaw } =
    useHasBulkTextAccess();
  const messagingEnabled = useMessagingFeaturesEnabled();
  const hasBulkTextAccess = messagingEnabled && hasBulkTextAccessRaw;
  const hasBulkTextFlagEnabled = messagingEnabled && hasBulkTextFlagEnabledRaw;

  const accessibleCampaignType = useMemo(() => {
    if (passedCampaignType === CampaignType.EMAIL && hasBulkEmailFlagEnabled) {
      return CampaignType.EMAIL;
    }
    if (passedCampaignType === CampaignType.SMS && hasBulkTextFlagEnabled) {
      return CampaignType.SMS;
    }
    if (hasBulkEmailFlagEnabled) {
      return CampaignType.EMAIL;
    }
    if (hasBulkTextFlagEnabled) {
      return CampaignType.SMS;
    }
    return undefined;
  }, [hasBulkEmailFlagEnabled, hasBulkTextFlagEnabled, passedCampaignType]);

  const navigateTo = useBulkNavigator(accessibleCampaignType);

  const changePage = (tab: string) => {
    const id = tab.replace(TABS_ID, '');
    navigateTo.campaigns(id as CampaignType);
  };

  return (
    <>
      <Page
        title={<Header title={t('All Campaigns')} />}
        subtitle={t(
          'Get the word out quick! Create the perfect campaign to keep your patients returning time and time again.'
        )}
      >
        {!accessibleCampaignType ? (
          <EmptyStateNoFeatures />
        ) : (
          <Tabs onChange={changePage} controlledTab={getTabId(accessibleCampaignType)}>
            {accessibleCampaignType === CampaignType.EMAIL && <DomainVerifyBanner />}
            <Tabs.Bar>
              {hasBulkEmailFlagEnabled && (
                <Tabs.Tab id={getTabId(CampaignType.EMAIL)} data-trackingid={`${BulkEmailPrefixes.Campaigns}-tab`}>
                  {t('Email Campaigns')}
                </Tabs.Tab>
              )}
              {hasBulkTextFlagEnabled && (
                <Tabs.Tab id={getTabId(CampaignType.SMS)} data-trackingid={`${BulkTextPrefixes.Campaigns}-tab`}>
                  {t('Text Campaigns')}
                </Tabs.Tab>
              )}
            </Tabs.Bar>
            {accessibleCampaignType === CampaignType.EMAIL && (
              <Tabs.Panel
                controller={getTabId(CampaignType.EMAIL)}
                css={{ paddingTop: hasBulkEmailAccess ? theme.spacing(4) : 0 }}
              >
                {hasBulkEmailAccess ? (
                  <CampaignList campaignType={CampaignType.EMAIL} />
                ) : (
                  <EmptyStateNoAccess campaignType={CampaignType.EMAIL} />
                )}
              </Tabs.Panel>
            )}
            {accessibleCampaignType === CampaignType.SMS && (
              <Tabs.Panel
                controller={getTabId(CampaignType.SMS)}
                css={{ paddingTop: hasBulkTextAccess ? theme.spacing(4) : 0 }}
              >
                {hasBulkTextAccess ? (
                  <CampaignList campaignType={CampaignType.SMS} />
                ) : (
                  <EmptyStateNoAccess campaignType={CampaignType.SMS} />
                )}
              </Tabs.Panel>
            )}
          </Tabs>
        )}
      </Page>
    </>
  );
};

const getTabId = (campaignType: CampaignType) => {
  return `${TABS_ID}${campaignType}`;
};
